import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { X, Info, Loader2, AlertTriangle } from "lucide-react";

/**
 * EHIProgramsPanel
 *
 * Drives the EHI Pricing rules section of the Quote Builder. The user picks one
 * or more programs (CCM / RPM / APCM / AWV); for each, they pick a single
 * pricing model and fill in the model-specific inputs. The component pulls the
 * catalog + rates from /api/quotes/ehi/catalog so rate changes never require a
 * frontend redeploy, then debounces a /api/quotes/ehi/compute call to display
 * itemized charges and pass them up via onComputedChange.
 *
 * Props:
 *   value:             [{program, model, inputs}]
 *   onChange:          fn(newValue) — user mutates programs/models/inputs
 *   onComputedChange:  fn({line_items, totals, warnings, rate_effective_date, source}) | null
 */
export default function EHIProgramsPanel({ value = [], onChange, onComputedChange }) {
  const { token } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [computed, setComputed] = useState(null);
  const [computing, setComputing] = useState(false);
  const [loadError, setLoadError] = useState("");

  // Load the catalog once on mount.
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const res = await axios.get(`${API}/quotes/ehi/catalog`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCatalog(res.data);
      } catch (e) {
        setLoadError("Couldn't load EHI catalog. Try refreshing.");
      }
    };
    fetchCatalog();
  }, [token]);

  // Debounced compute on every change.
  useEffect(() => {
    if (!value || value.length === 0) {
      setComputed(null);
      if (onComputedChange) onComputedChange(null);
      return;
    }
    const t = setTimeout(async () => {
      setComputing(true);
      try {
        const res = await axios.post(
          `${API}/quotes/ehi/compute`,
          { programs: value },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setComputed(res.data);
        if (onComputedChange) onComputedChange(res.data);
      } catch (e) {
        toast.error("EHI compute failed");
      } finally {
        setComputing(false);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value), token]);

  // Programs added from the catalog arrive with model="" — auto-default to the
  // first model in the catalog so the inputs render immediately.
  useEffect(() => {
    if (!catalog) return;
    const needsDefault = (value || []).some(v => !v.model);
    if (!needsDefault) return;
    onChange((value || []).map(v => {
      if (v.model) return v;
      const models = Object.keys(catalog.catalog?.[v.program]?.models || {});
      return { ...v, model: models[0] || "" };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, value]);

  const removeProgram = useCallback((programKey) => {
    onChange((value || []).filter(v => v.program !== programKey));
  }, [value, onChange]);

  const updateModel = useCallback((programKey, newModel) => {
    onChange((value || []).map(v => v.program === programKey ? { ...v, model: newModel, inputs: {} } : v));
  }, [value, onChange]);

  const updateInput = useCallback((programKey, inputKey, newValue) => {
    onChange((value || []).map(v => {
      if (v.program !== programKey) return v;
      return { ...v, inputs: { ...v.inputs, [inputKey]: newValue } };
    }));
  }, [value, onChange]);

  if (loadError) {
    return (
      <div data-testid="ehi-load-error" style={{ padding: 12, background: "var(--red-light)", borderRadius: 8, fontSize: 12, color: "var(--red)" }}>
        {loadError}
      </div>
    );
  }
  if (!catalog) {
    return (
      <div style={{ padding: 12, color: "var(--text-tertiary)", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
        <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> Loading EHI catalog…
      </div>
    );
  }

  if (!value || value.length === 0) return null;

  return (
    <div data-testid="ehi-programs-panel" style={{ marginBottom: 20, border: "0.5px solid var(--border)", borderRadius: 10, overflow: "hidden", background: "var(--surface)" }}>
      {/* Header */}
      <div style={{ padding: "10px 14px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--page-bg)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 650 }}>EHI Programs</div>
          <span style={{ fontSize: 9, color: "var(--text-tertiary)", padding: "2px 6px", borderRadius: 4, background: "var(--page-bg)", border: "0.5px solid var(--border)" }}>
            Rates effective {catalog.rate_effective_date}
          </span>
          {computing && <Loader2 style={{ width: 11, height: 11, animation: "spin 0.8s linear infinite", color: "var(--blue)" }} />}
        </div>
        <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{catalog.source}</span>
      </div>

      {/* Per-program cards */}
      {(value || []).map((sel) => {
        const program = catalog.catalog[sel.program];
        if (!program) return null;
        const model = program.models[sel.model];
        return (
          <div key={sel.program} data-testid={`ehi-card-${sel.program}`} style={{ padding: 14, borderBottom: "0.5px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{program.label}</div>
              <button onClick={() => removeProgram(sel.program)} data-testid={`ehi-remove-${sel.program}`} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 2 }}>
                <X style={{ width: 13, height: 13 }} />
              </button>
            </div>

            {/* Model picker */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3 }}>
                Pricing model (one per program)
              </label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {Object.entries(program.models).map(([mk, m]) => {
                  const active = sel.model === mk;
                  return (
                    <button
                      key={mk}
                      data-testid={`ehi-model-${sel.program}-${mk}`}
                      onClick={() => updateModel(sel.program, mk)}
                      style={{
                        fontSize: 11, padding: "5px 10px", cursor: "pointer", borderRadius: 6,
                        background: active ? "rgba(0,113,227,0.10)" : "#fff",
                        color: active ? "#0071e3" : "var(--text-primary)",
                        border: `0.5px solid ${active ? "#0071e3" : "var(--border)"}`,
                      }}>
                      {m.label}
                    </button>
                  );
                })}
              </div>
              {model?.description && (
                <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 6, fontStyle: "italic", display: "flex", alignItems: "center", gap: 4 }}>
                  <Info style={{ width: 10, height: 10 }} />
                  {model.description}
                </div>
              )}
            </div>

            {/* Percentage / payer-reimbursement model: informational only */}
            {model?.percentage_only && (
              <div
                data-testid={`ehi-percentage-info-${sel.program}`}
                style={{ marginBottom: 12, padding: "12px 14px", background: "linear-gradient(135deg, rgba(0,113,227,0.05), rgba(124,58,237,0.04))", border: "0.5px solid rgba(0,113,227,0.2)", borderRadius: 8 }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <Info style={{ width: 14, height: 14, color: "#0071e3", flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#0071e3", letterSpacing: 0.3, textTransform: "uppercase", marginBottom: 4 }}>
                      Percentage pricing model
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>
                      {model.percentage_label || "50% of payer reimbursement"}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.5 }}>
                      Enable Healthcare keeps a percentage of whatever the payer reimburses for this program. Because every payer pays a different amount, a hard dollar number cannot be quoted up front — only the percentage.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Inputs (strip patient-count fields per Section 2B follow-up:
                fixed-price models capture only the flat fixed price — the
                quote no longer carries a patient-count tally) */}
            {model && !model.percentage_only && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {(model.inputs || []).filter(inp => !/patient_count|patients|pmpm_patients/i.test(inp.key || "")).map(inp => (
                  <div key={inp.key}>
                    <label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", display: "block", marginBottom: 3 }}>
                      {inp.label}
                      {inp.rate != null && (
                        <span style={{ marginLeft: 6, fontSize: 9, color: "#0071e3", fontWeight: 700 }}>
                          @ ${inp.rate}{inp.rate_label || ""}
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step={inp.type === "currency" ? "0.01" : "1"}
                      value={sel.inputs?.[inp.key] ?? ""}
                      onChange={e => updateInput(sel.program, inp.key, e.target.value)}
                      style={{ fontSize: 12, height: 30, padding: "4px 8px", width: "100%", borderRadius: 6, border: "0.5px solid var(--border)" }}
                      data-testid={`ehi-input-${sel.program}-${inp.key}`}
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Warnings (hard rules: 99439 needs 99490, G0557 xor G0558, etc.) */}
      {computed?.warnings?.length > 0 && (
        <div style={{ padding: "10px 14px", background: "rgba(255,159,10,0.10)", borderTop: "0.5px solid rgba(255,159,10,0.4)", display: "flex", flexDirection: "column", gap: 4 }} data-testid="ehi-warnings">
          {computed.warnings.map((w, i) => (
            <div key={i} style={{ fontSize: 11, color: "#8a5500", display: "flex", alignItems: "flex-start", gap: 5 }}>
              <AlertTriangle style={{ width: 11, height: 11, marginTop: 2, flexShrink: 0 }} />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Totals summary */}
      {computed?.line_items?.length > 0 && (
        <div data-testid="ehi-totals" style={{ padding: "12px 14px", background: "rgba(0,113,227,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 11, color: "#0071e3", fontWeight: 600 }}>
            EHI total: {computed.line_items.length} line item{computed.line_items.length === 1 ? "" : "s"}
          </div>
          <div style={{ textAlign: "right" }}>
            {computed.totals.one_time > 0 && (
              <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                One-time: <strong style={{ color: "var(--text-primary)" }}>${computed.totals.one_time.toLocaleString()}</strong>
              </div>
            )}
            {computed.totals.monthly_recurring > 0 && (
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0071e3" }}>
                ${computed.totals.monthly_recurring.toLocaleString()}/mo
              </div>
            )}
            {computed.totals.annual_recurring > 0 && (
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0071e3" }}>
                ${computed.totals.annual_recurring.toLocaleString()}/yr
              </div>
            )}
            {computed.totals.annual_estimate > 0 && (
              <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                First-year recurring ~${computed.totals.annual_estimate.toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
